'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConnectorFilePickerContext } from '#pplx/frontend/apps/ask/connectors-file-pickers/useConnectorFilePickerContext.js';
import { ModalLoader } from '#pplx/frontend/apps/ask/modals/ModalLoader.js';
import { useBoxPicker } from '#pplx/frontend/components/hooks/useBoxPicker.js';
import { useMicrosoftPicker } from '#pplx/frontend/components/hooks/useMicrosoftPickerSupport.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
const MicrosoftFilePickerModal = Dynamic(async () => {
    const { MicrosoftFilePickerModal } = await import('#pplx/frontend/apps/ask/microsoft/MicrosoftFilePickerModal.js');
    return { default: MicrosoftFilePickerModal };
}, { loading: () => _jsx(ModalLoader, {}) });
const BoxFilePickerModal = Dynamic(async () => {
    const { BoxFilePickerModal } = await import('#pplx/frontend/apps/ask/box-files/BoxFilePickerModal.js');
    return { default: BoxFilePickerModal };
}, { loading: () => _jsx(ModalLoader, {}) });
export const ConnectorFilePickerProvider = React.memo(function ConnectorFilePickerProvider({ children }) {
    const [microsoftModalState, setMicrosoftModalState] = useState({
        isOpen: false,
        options: null,
    });
    const { openPicker: openMicrosoftPickerSDK } = useMicrosoftPicker();
    // Open the SDK picker after the modal has rendered
    useEffect(() => {
        if (microsoftModalState.isOpen &&
            microsoftModalState.options &&
            // Only open SDK if we're not selecting a SharePoint site
            !(microsoftModalState.options.connector === 'sharepoint' &&
                !microsoftModalState.options.webUrl)) {
            // Small delay to ensure iframe is in DOM
            const timeoutId = setTimeout(() => {
                openMicrosoftPickerSDK(microsoftModalState.options);
            }, 50);
            return () => clearTimeout(timeoutId);
        }
        return undefined;
    }, [microsoftModalState.isOpen, microsoftModalState.options, openMicrosoftPickerSDK]);
    const handleMicrosoftModalClose = useCallback(() => {
        setMicrosoftModalState({ isOpen: false, options: null });
    }, []);
    const handleMicrosoftPickerSiteSelect = useCallback((siteWebUrl) => {
        if (microsoftModalState.options) {
            const updatedOptions = {
                ...microsoftModalState.options,
                webUrl: siteWebUrl,
            };
            setMicrosoftModalState({ isOpen: true, options: updatedOptions });
        }
    }, [microsoftModalState.options]);
    const openMicrosoftPicker = useCallback((options) => {
        setMicrosoftModalState({ isOpen: true, options });
    }, []);
    // --- Box picker state ---
    const [boxModalOpen, setBoxModalOpen] = useState(false);
    const boxApiKeyRef = useRef('');
    const boxOnPickedRef = useRef(null);
    const boxOnErrorRef = useRef(null);
    const handleBoxModalClose = useCallback(() => {
        setBoxModalOpen(false);
        boxOnPickedRef.current = null;
        boxOnErrorRef.current = null;
    }, []);
    const handleBoxPicked = useCallback((items) => {
        boxOnPickedRef.current?.(items);
        handleBoxModalClose();
    }, [handleBoxModalClose]);
    const handleBoxCancel = useCallback(() => {
        handleBoxModalClose();
    }, [handleBoxModalClose]);
    const handleBoxError = useCallback((_error) => {
        boxOnErrorRef.current?.();
        handleBoxModalClose();
    }, [handleBoxModalClose]);
    const { openPicker: openBoxPickerSDK, cleanupPicker: cleanupBoxPicker } = useBoxPicker({
        mode: 'attachment',
        isAudioVideoFilesEnabled: true,
        onPicked: handleBoxPicked,
        onCancel: handleBoxCancel,
        onError: handleBoxError,
    });
    useEffect(() => {
        if (boxModalOpen) {
            // Small delay to ensure the container div is in the DOM
            const timeoutId = setTimeout(() => {
                openBoxPickerSDK({ apiKey: boxApiKeyRef.current });
            }, 50);
            return () => clearTimeout(timeoutId);
        }
        return undefined;
    }, [boxModalOpen, openBoxPickerSDK]);
    const openBoxPicker = useCallback((args) => {
        boxApiKeyRef.current = args.apiKey;
        boxOnPickedRef.current = args.onPicked;
        boxOnErrorRef.current = args.onError ?? null;
        setBoxModalOpen(true);
    }, []);
    const contextValue = useMemo(() => ({
        openMicrosoftPicker,
        closeMicrosoftPicker: handleMicrosoftModalClose,
        openBoxPicker,
        closeBoxPicker: handleBoxModalClose,
        isPickerOpen: microsoftModalState.isOpen || boxModalOpen,
    }), [
        openMicrosoftPicker,
        handleMicrosoftModalClose,
        openBoxPicker,
        handleBoxModalClose,
        microsoftModalState.isOpen,
        boxModalOpen,
    ]);
    return (_jsxs(ConnectorFilePickerContext.Provider, { value: contextValue, children: [children, microsoftModalState.isOpen && (_jsx(MicrosoftFilePickerModal, { isOpen: microsoftModalState.isOpen, options: microsoftModalState.options, onClose: handleMicrosoftModalClose, onSelectSite: handleMicrosoftPickerSiteSelect })), boxModalOpen && (_jsx(BoxFilePickerModal, { isOpen: boxModalOpen, onClose: handleBoxModalClose, cleanupPicker: cleanupBoxPicker }))] }));
});
//# sourceMappingURL=ConnectorFilePickerProvider.js.map